/**
 * The SmartSpaza administration API.
 *
 * One repository for the whole control plane rather than one per resource: the
 * admin portal is a single screen family read by a handful of people, and eight
 * files that each export three functions would be harder to find things in than
 * one that is grouped.
 *
 * Everything here is `/api/v1/admin/*`, which the backend gates twice — platform
 * staff, then a specific platform permission. Nothing in this file is a security
 * boundary; a 403 from the server is.
 */

import { api } from "@/api/client";

// ═══════════════════════════════════════════════════════════════════════════════
// Shapes
// ═══════════════════════════════════════════════════════════════════════════════
export interface Page<T> {
  count: number;
  page: number;
  pages: number;
  results: T[];
}

export type MerchantStatus =
  | "APPROVED"
  | "TRIAL"
  | "ACTIVE"
  | "PAST_DUE"
  | "SUSPENDED"
  | "CANCELLED";

export interface MerchantRow {
  id: string;
  merchant_number: string;
  name: string;
  trading_name: string;
  slug: string;
  status: MerchantStatus;
  owner: string;
  package: string | null;
  subscription_status: string | null;
  branch_count: number;
  user_count: number;
  /** A decimal string. Money never crosses the wire as a JSON number. */
  outstanding: string;
  province: string | null;
  created_at: string;
}

export interface MerchantDetail extends Omit<MerchantRow, "owner" | "package"> {
  is_active: boolean;
  industry_type: string;
  email: string;
  phone: string;
  whatsapp: string;
  registration_number: string;
  vat_number: string;
  vat_mode: string;
  address: string;
  municipality: string | null;
  currency: string;
  timezone: string;
  suspension_reason: string;
  onboarded_at: string | null;
  approved_at: string | null;
  activated_at: string | null;
  suspended_at: string | null;
  cancelled_at: string | null;
  /** What stands between this merchant and trading. Empty means ready. */
  activation_blockers: string[];
}

export type ApplicationStatus =
  | "APPLICATION"
  | "PENDING_REVIEW"
  | "PENDING_DOCUMENTS"
  | "APPROVED"
  | "REJECTED";

export interface ApplicationRow {
  id: string;
  reference: string;
  status: ApplicationStatus;
  trading_name: string;
  contact_name: string;
  contact_email: string;
  phone: string;
  province: string | null;
  expected_branches: number;
  expected_tills: number;
  submitted_at: string | null;
}

export interface ApplicationDetail extends ApplicationRow {
  legal_name: string;
  registration_number: string;
  tax_number: string;
  business_type: string;
  description: string;
  email: string;
  website: string;
  contact_mobile: string;
  preferred_channel: string;
  address: string;
  needs_geocoding: boolean;
  expected_users: number;
  expected_monthly_volume: number | null;
  needs_inventory: boolean;
  needs_procurement: boolean;
  needs_reporting: boolean;
  decision_reason: string;
  reviewed_at: string | null;
  merchant_number: string | null;
  events: { id: string; event: string; note: string; actor_name: string; created_at: string }[];
  documents: { id: string; kind: string; original_name: string; size_bytes: number }[];
}

export interface PackageRow {
  id: number;
  code: string;
  name: string;
  description: string;
  monthly_price: string;
  annual_price: string | null;
  currency: string;
  trial_days: number;
  is_active: boolean;
  is_public: boolean;
  display_order: number;
  features: string[];
  limits: Record<string, number | null>;
  merchant_count: number;
}

export interface InvoiceRow {
  id: string;
  number: string;
  merchant: string;
  merchant_name: string;
  merchant_number: string;
  status: string;
  issue_date: string | null;
  due_date: string | null;
  currency: string;
  total: string;
  amount_paid: string;
  balance: string;
}

export interface UsageLine {
  key: string;
  label: string;
  used: number;
  limit: number | null;
  remaining: number | null;
  percent: number | null;
  is_unlimited: boolean;
  is_exhausted: boolean;
}

export interface Dashboard {
  merchants: { total: number; active: number; trial: number; past_due: number; suspended: number };
  applications: { pending: number; awaiting_documents: number };
  subscriptions: { active: number; trial: number };
  billing: { outstanding: string; overdue: string; collected: string };
  by_province: { label: string; count: number; unallocated: boolean }[];
}

export interface PackageRequestRow {
  id: string;
  merchant: string;
  merchant_name: string;
  merchant_number: string;
  current_package: string | null;
  requested_package: string;
  requested_package_code: string;
  change_kind: "UPGRADE" | "DOWNGRADE" | "LATERAL";
  status: "PENDING" | "ACTIONED" | "DECLINED" | "WITHDRAWN";
  /** What the merchant typed. Usually the most useful field on the row. */
  note: string;
  requested_by: string;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string;
  resolution_note: string;
}

export interface AuditRow {
  id: string;
  action: string;
  actor_name: string;
  actor_roles: string;
  merchant_label: string;
  entity_label: string;
  reason: string;
  created_at: string;
}

export interface PlatformRole {
  id: number;
  code: string;
  name: string;
  description: string;
  permissions: string[];
}

export interface PlatformStaff {
  id: string;
  staff_code: string;
  display_name: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string;
  is_active: boolean;
  is_superuser: boolean;
  last_login_at: string | null;
  roles: string[];
  /** False until they have followed their set-password link. */
  has_password: boolean;
  is_you: boolean;
}

/** One row of a merchant's Users tab. */
export interface MerchantUser {
  id: string;
  staff_code: string;
  name: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string;
  is_active: boolean;
  last_login_at: string | null;
}

/** What an administrator may correct about somebody. Nothing role-shaped. */
export interface UserDetails {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

export interface NewPlatformStaff {
  first_name: string;
  last_name: string;
  email: string;
  roles: string[];
}

// ═══════════════════════════════════════════════════════════════════════════════
// Calls
// ═══════════════════════════════════════════════════════════════════════════════
function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) search.set(key, value);
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

export const adminRepository = {
  dashboard: () => api.get<Dashboard>("/admin/dashboard/"),

  // ── Merchants ───────────────────────────────────────────────────────────────
  merchants: (filters: {
    search?: string | undefined;
    status?: string | undefined;
    province?: string | undefined;
    billing?: string | undefined;
    page?: string | undefined;
  } = {}) => api.get<Page<MerchantRow>>(`/admin/merchants/${query(filters)}`),

  merchant: (id: string) => api.get<MerchantDetail>(`/admin/merchants/${id}/`),

  /** Start a read-only support view of a shop. The reason is shown to its owner. */
  openSupportView: (id: string, reason: string) =>
    api.post<{ merchant_id: string; merchant_name: string; expires_at: string }>(
      `/admin/merchants/${id}/support-session/`,
      { reason },
    ),

  endSupportView: (id: string) => api.delete<void>(`/admin/merchants/${id}/support-session/`),

  merchantUsage: (id: string) => api.get<UsageLine[]>(`/admin/merchants/${id}/usage/`),

  merchantBranches: (id: string) =>
    api.get<
      {
        id: string;
        name: string;
        code: string;
        is_primary: boolean;
        is_active: boolean;
        address: string;
        province: string | null;
      }[]
    >(`/admin/merchants/${id}/branches/`),

  merchantUsers: (id: string) => api.get<MerchantUser[]>(`/admin/merchants/${id}/users/`),

  updateMerchantUser: (merchantId: string, userId: string, details: UserDetails) =>
    api.patch<MerchantUser>(`/admin/merchants/${merchantId}/users/${userId}/`, details),

  /** For a shop user locked out of their account. Signs them out everywhere. */
  setMerchantUserPassword: (merchantId: string, userId: string, password: string) =>
    api.post<void>(`/admin/merchants/${merchantId}/users/${userId}/set-password/`, { password }),

  merchantAudit: (id: string) => api.get<AuditRow[]>(`/admin/merchants/${id}/audit/`),

  activateMerchant: (id: string) =>
    api.post<MerchantDetail>(`/admin/merchants/${id}/activate/`, {}),

  suspendMerchant: (id: string, reason: string) =>
    api.post<MerchantDetail>(`/admin/merchants/${id}/suspend/`, { reason }),

  reactivateMerchant: (id: string) =>
    api.post<MerchantDetail>(`/admin/merchants/${id}/reactivate/`, {}),

  // ── Applications ────────────────────────────────────────────────────────────
  applications: (filters: { status?: string | undefined; search?: string | undefined; page?: string | undefined } = {}) =>
    api.get<Page<ApplicationRow>>(`/admin/applications/${query(filters)}`),

  application: (id: string) => api.get<ApplicationDetail>(`/admin/applications/${id}/`),

  approveApplication: (id: string) =>
    api.post<MerchantDetail>(`/admin/applications/${id}/approve/`, {}),

  rejectApplication: (id: string, reason: string) =>
    api.post<ApplicationDetail>(`/admin/applications/${id}/reject/`, { reason }),

  requestInformation: (id: string, note: string) =>
    api.post<ApplicationDetail>(`/admin/applications/${id}/request-information/`, { note }),

  /** A fresh set-password link for an approved owner whose first one expired. */
  resendPasswordLink: (id: string) =>
    api.post<void>(`/admin/applications/${id}/resend-password-link/`, {}),

  // ── Commercial ──────────────────────────────────────────────────────────────
  packages: () => api.get<Page<PackageRow>>("/admin/packages/"),

  deactivatePackage: (id: number, reason: string) =>
    api.post<PackageRow>(`/admin/packages/${id}/deactivate/`, { reason }),

  reactivatePackage: (id: number) => api.post<PackageRow>(`/admin/packages/${id}/reactivate/`, {}),

  /**
   * What merchants have asked to move to (phase T10).
   *
   * Defaults to the open ones server-side. Pass `status: "ALL"` for the history.
   */
  packageRequests: (filters: { status?: string | undefined; search?: string | undefined } = {}) =>
    api.get<Page<PackageRequestRow>>(`/admin/package-requests/${query(filters)}`),

  /**
   * Mark a request dealt with. Deliberately does **not** move the package —
   * that is `changePackage` on the merchant's subscription, so an administrator
   * who agreed to something other than what was asked records what actually
   * happened rather than what was requested.
   */
  resolvePackageRequest: (id: string, status: "ACTIONED" | "DECLINED", reason: string) =>
    api.post<PackageRequestRow>(`/admin/package-requests/${id}/resolve/`, { status, reason }),

  // ── Billing ─────────────────────────────────────────────────────────────────
  invoices: (filters: { status?: string | undefined; search?: string | undefined; merchant?: string | undefined; page?: string | undefined } = {}) =>
    api.get<Page<InvoiceRow>>(`/admin/invoices/${query(filters)}`),

  voidInvoice: (id: string, reason: string) =>
    api.post<InvoiceRow>(`/admin/invoices/${id}/void/`, { reason }),

  recordPayment: (id: string, amount: string, method = "EFT", external_reference = "") =>
    api.post<unknown>(`/admin/invoices/${id}/record-payment/`, {
      amount,
      method,
      external_reference,
    }),

  // ── SmartSpaza staff ────────────────────────────────────────────────────────
  staff: () => api.get<PlatformStaff[]>("/admin/platform-users/"),

  platformRoles: () => api.get<Page<PlatformRole>>("/admin/platform-roles/"),

  /** Returns a set-password link as well, because production email may not reach them. */
  addStaff: (body: NewPlatformStaff) =>
    api.post<PlatformStaff & { set_password_link: string }>("/admin/platform-users/", body),

  updateStaff: (id: string, details: UserDetails) =>
    api.patch<PlatformStaff>(`/admin/platform-users/${id}/`, details),

  grantRole: (id: string, role: string) =>
    api.post<PlatformStaff>(`/admin/platform-users/${id}/grant-role/`, { role }),

  revokeRole: (id: string, role: string) =>
    api.post<PlatformStaff>(`/admin/platform-users/${id}/revoke-role/`, { role }),

  deactivateStaff: (id: string) =>
    api.post<PlatformStaff>(`/admin/platform-users/${id}/deactivate/`, {}),

  reactivateStaff: (id: string) =>
    api.post<PlatformStaff>(`/admin/platform-users/${id}/reactivate/`, {}),

  staffPasswordLink: (id: string) =>
    api.post<{ set_password_link: string }>(`/admin/platform-users/${id}/password-link/`, {}),

  // ── Oversight ───────────────────────────────────────────────────────────────
  auditLog: (filters: { action?: string | undefined; search?: string | undefined; page?: string | undefined } = {}) =>
    api.get<Page<AuditRow>>(`/admin/audit-logs/${query(filters)}`),
};
